<template>
    <section class="products-page section section-lg bg-default">
        <div class="container container-wide">
            <header class="products-page__hero ui-stack ui-stack--sm">
                <h1 class="products-page__title">コース選択</h1>
                <p class="products-page__lead">
                    申し込むコースをお選びください。ログインしていない場合は、次の画面でログインが必要です。
                </p>
            </header>

            <div v-if="activeNotification" class="products-page__alerts" role="alert">
                <div class="products-page__alert-card ui-card">
                    <span class="products-page__alert-icon" aria-hidden="true"></span>
                    <div class="products-page__alert-content ui-stack ui-stack--xs">
                        <p class="products-page__alert-message">{{ activeNotification.message }}</p>
                        <div class="products-page__alert-links">
                            <router-link class="products-page__alert-link" to="/account">詳細を見る</router-link>
                            <span class="products-page__alert-note">（アカウントページ）</span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="additionalNotifications.length" class="products-page__alerts-sr sr-only" aria-hidden="true">
                <span v-for="(item, index) in additionalNotifications" :key="index">{{ item.message }}</span>
            </div>

            <div v-if="pageLoadingIndicator" class="products-page__loading">
                <img src="/images/eowp/animation/loading-spinner.jpg" alt="読み込み中" />
            </div>

            <div v-else class="products-page__plans ui-stack ui-stack--xl">
                <div class="products-page__plan-grid">
                    <article class="products-page__plan-card ui-card" :class="planCardClasses('yearly')">
                        <div class="products-page__plan-ribbon">おすすめ</div>
                        <header class="products-page__plan-head ui-stack ui-stack--xs">
                            <p class="products-page__plan-name">年額コース</p>
                            <p class="products-page__plan-price">{{ eowp.yearly.merchantPrice }}円（税込）</p>
                            <p class="products-page__plan-price-note">月換算 {{ eowp.yearly.monthlyPrice }}円</p>
                        </header>
                        <ul class="products-page__plan-benefits">
                            <li>
                                <img src="/images/eowp/bullet-square.png" class="products-page__plan-bullet" alt="" />
                                <span>1年分をまとめて支払えるコース</span>
                            </li>
                            <li>
                                <img src="/images/eowp/bullet-square.png" class="products-page__plan-bullet" alt="" />
                                <span>月額コース1年分（{{ priceComparison.monthlyYearlyTotal }}円）より{{ priceComparison.yearlySavings }}円お得</span>
                            </li>
                        </ul>
                        <footer class="products-page__plan-footer ui-stack ui-stack--md">
                            <div class="products-page__plan-actions" v-if="eowp.showButtons">
                                <button
                                    class="ui-button ui-button--outline products-page__plan-button"
                                    :disabled="isPlanDisabled(eowp.yearly)"
                                    :title="buttonTooltip(eowp.yearly)"
                                    type="button"
                                    @click="submit(eowp.yearly.itemId, 'yearly_product_click')"
                                >
                                    {{ buyAnnualButtonText }}
                                </button>
                            </div>
                            <p class="products-page__plan-billing">利⽤料⾦：{{ eowp.yearly.merchantPrice }}円（税込）／１年</p>
                        </footer>
                    </article>

                    <article class="products-page__plan-card ui-card" :class="planCardClasses('monthly')">
                        <header class="products-page__plan-head ui-stack ui-stack--xs">
                            <p class="products-page__plan-name">月額コース</p>
                            <p class="products-page__plan-price">{{ eowp.monthly.merchantPrice }}円（税込）</p>
                            <p class="products-page__plan-price-note">{{ eowp.monthly.basePrice }}円+税</p>
                        </header>
                        <ul class="products-page__plan-benefits">
                            <li>
                                <img src="/images/eowp/bullet-square.png" class="products-page__plan-bullet" alt="" />
                                <span>月々のお支払いで利用できるコース</span>
                            </li>
                            <li>
                                <img src="/images/eowp/bullet-square.png" class="products-page__plan-bullet" alt="" />
                                <span>いつでも年額へ切り替え可能</span>
                            </li>
                        </ul>
                        <footer class="products-page__plan-footer ui-stack ui-stack--md">
                            <div class="products-page__plan-actions" v-if="eowp.showButtons">
                                <button
                                    class="ui-button ui-button--outline products-page__plan-button"
                                    :disabled="isPlanDisabled(eowp.monthly)"
                                    :title="buttonTooltip(eowp.monthly)"
                                    type="button"
                                    @click="submit(eowp.monthly.itemId, 'monthly_product_click')"
                                >
                                    月額で申し込む
                                </button>
                            </div>
                            <p class="products-page__plan-billing">利⽤料⾦：{{ eowp.monthly.merchantPrice }}円（税込）／１カ月</p>
                        </footer>
                    </article>
                </div>

                <section class="products-page__value-card ui-card ui-stack ui-stack--lg">
                    <div class="products-page__value-intro ui-stack ui-stack--xs">
                        <h2 class="products-page__value-eyebrow">英辞郎 on the WEB Proにアップグレードすると…</h2>
                    </div>
                    <ul class="products-page__value-benefits">
                        <li
                            v-for="benefit in valueCardBenefits"
                            :key="benefit.title"
                            class="products-page__value-benefit"
                        >
                            <img
                                class="products-page__value-icon"
                                :src="benefit.image"
                                :alt="benefit.alt"
                            />
                            <div class="products-page__value-copy">
                                <h3 class="products-page__value-benefit-title">{{ benefit.title }}</h3>
                                <p class="products-page__value-benefit-text">{{ benefit.description }}</p>
                            </div>
                        </li>
                    </ul>
                </section>
            </div>

            <section class="products-page__details ui-card ui-stack ui-stack--lg" id="supporting-details">
                <header class="products-page__details-header ui-stack ui-stack--xs">
                    <h2 class="products-page__details-title">詳しいご案内</h2>
                </header>

                <div class="products-page__accordion">
                    <div class="products-page__accordion-item">
                        <button class="products-page__accordion-toggle" @click="toggleAccordion('trial')" :aria-expanded="accordionState.trial">
                            <span class="products-page__accordion-label">無料体験</span>
                            <img
                                class="products-page__accordion-icon"
                                :class="{ 'products-page__accordion-icon--open': accordionState.trial }"
                                src="/images/down-chevron.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                        <transition name="products-accordion">
                            <div class="products-page__accordion-body" v-show="accordionState.trial">
                                <p><b>{{ ShopConstants.FREE_TRIAL_DURATION }}無料（初めてのお申し込みの場合）について</b><br />無料期間中はいつでもキャンセル可能です。</p>
                            </div>
                        </transition>
                    </div>

                    <div class="products-page__accordion-item">
                        <button class="products-page__accordion-toggle" @click="toggleAccordion('featureDifference')" :aria-expanded="accordionState.featureDifference">
                            <span class="products-page__accordion-label">他サービスとの機能の違い</span>
                            <img
                                class="products-page__accordion-icon"
                                :class="{ 'products-page__accordion-icon--open': accordionState.featureDifference }"
                                src="/images/down-chevron.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                        <transition name="products-accordion">
                            <div class="products-page__accordion-body" v-show="accordionState.featureDifference">
                                <p>
                                    <b>他サービスとの機能の違いについて</b><br />「英辞郎 on the WEB Pro」は、広告無し、収録データや機能に違いがあります。<br />各サービスの違いは
                                    <a class="products-page__inline-link" target="_blank" href="https://eow-support.alc.co.jp/hc/ja/articles/30033256437017">こちら</a>
                                    をご確認ください。<br />※「英辞郎 on the WEB Pro」サイトにおいて、会員特典（弊社サービスが割引利用・購入できるクーポンなど）のご案内の画像やテキストが入ることがあります。
                                </p>
                            </div>
                        </transition>
                    </div>

                    <div class="products-page__accordion-item">
                        <button class="products-page__accordion-toggle" @click="toggleAccordion('payment')" :aria-expanded="accordionState.payment">
                            <span class="products-page__accordion-label">お支払い</span>
                            <img
                                class="products-page__accordion-icon"
                                :class="{ 'products-page__accordion-icon--open': accordionState.payment }"
                                src="/images/down-chevron.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                        <transition name="products-accordion">
                            <div class="products-page__accordion-body" v-show="accordionState.payment">
                                <p><b>お⽀払いについて</b><br />本ショップでのご購⼊はクレジットカードによるお⽀払いに限らせていただいており、カード決済はStripeが⾏います。</p>
                                <div class="products-page__annotation">《<a class="products-page__inline-link" target="_blank" href="/tokusho">特定商取引に関する法律に基づく表示</a>》</div>
                                <ul class="products-page__list">
                                    <li>
                                        <span>年額コースの場合</span><br />
                                        <span>利⽤料⾦：{{ eowp.yearly.merchantPrice }}円（税込）／１年</span><br />
                                        <span>利⽤期⽇：アカウントページ「サービス情報詳細」画⾯に記載されます。サブスクリプションは年毎に⾃動で更新され、利⽤停⽌⼿続きをされた場合でもアカウントページ「サービス情報詳細」画⾯に記載されている期⽇まで利⽤可能です。期間中の解約による払い戻しはいたしかねます。</span><br />
                                        <span>※初めてのお申し込みの場合は{{ ShopConstants.FREE_TRIAL_DURATION }}無料です。{{ ShopConstants.FREE_TRIAL_DURATION }}の無料期間内に利用停止手続きをされると、利用料金はかかりません。</span>
                                    </li>
                                    <li>
                                        <span>⽉額コースの場合</span><br />
                                        <span>利⽤料⾦：{{ eowp.monthly.merchantPrice }}円（税込）／１カ月</span><br />
                                        <span>利用期日：アカウントページ「サービス情報詳細」画面に記載されます。サブスクリプションは月毎に自動で更新され、利用停止手続きをされた場合でもアカウントページ「サービス情報詳細」画面に記載されている期日まで利用可能です。期間中の解約による払い戻しはいたしかねます。</span><br />
                                        <span>※初めてのお申し込みの場合は{{ ShopConstants.FREE_TRIAL_DURATION }}無料です。{{ ShopConstants.FREE_TRIAL_DURATION }}の無料期間内に利用停止手続きをされると、利用料金はかかりません。</span>
                                    </li>
                                </ul>
                            </div>
                        </transition>
                    </div>

                    <div class="products-page__accordion-item">
                        <button class="products-page__accordion-toggle" @click="toggleAccordion('cancellation')" :aria-expanded="accordionState.cancellation">
                            <span class="products-page__accordion-label">解約</span>
                            <img
                                class="products-page__accordion-icon"
                                :class="{ 'products-page__accordion-icon--open': accordionState.cancellation }"
                                src="/images/down-chevron.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                        <transition name="products-accordion">
                            <div class="products-page__accordion-body" v-show="accordionState.cancellation">
                                <p><b>年額コース、月額コースの解約について</b></p>
                                <p>アカウントページからお手続きいただけます。アプリ内購入につきましては、それぞれの購入先でお手続きをお願いいたします。</p>
                            </div>
                        </transition>
                    </div>

                    <div class="products-page__accordion-item">
                        <button class="products-page__accordion-toggle" @click="toggleAccordion('overseas')" :aria-expanded="accordionState.overseas">
                            <span class="products-page__accordion-label">海外利用</span>
                            <img
                                class="products-page__accordion-icon"
                                :class="{ 'products-page__accordion-icon--open': accordionState.overseas }"
                                src="/images/down-chevron.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                        <transition name="products-accordion">
                            <div class="products-page__accordion-body" v-show="accordionState.overseas">
                                <p><b>⽇本国外からの利⽤について</b></p>
                                <p>⽇本国外で「英辞郎 on the WEB Pro」サービスを購⼊された場合も、ご利用料⾦は、⽇本国内と同額（の税込み額）となります。 なお、本サービスの各種更新時間・課⾦基準時点などの定めは、⽇本標準時間に従います。</p>
                            </div>
                        </transition>
                    </div>

                    <div class="products-page__accordion-item">
                        <button class="products-page__accordion-toggle" @click="toggleAccordion('environment')" :aria-expanded="accordionState.environment">
                            <span class="products-page__accordion-label">推奨環境</span>
                            <img
                                class="products-page__accordion-icon"
                                :class="{ 'products-page__accordion-icon--open': accordionState.environment }"
                                src="/images/down-chevron.svg"
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                        <transition name="products-accordion">
                            <div class="products-page__accordion-body" v-show="accordionState.environment">
                                <p><b>動作環境</b></p>
                                <p>「英辞郎 on the WEB Pro」は、下記のWebブラウザー環境でのご利⽤を推奨します。</p>
                                <ul class="products-page__list">
                                    <li>
                                        <span>デスクトップ版 推奨環境</span>
                                        <ul class="products-page__sublist">
                                            <li>Windows：{{ ShopConstants.BROWSER_RECOMMENDED_VERSION_WINDOWS }}</li>
                                            <li>macOS：{{ ShopConstants.BROWSER_RECOMMENDED_VERSION_MACOS }}</li>
                                        </ul>
                                    </li>
                                    <li>
                                    <span>スマートフォン版 推奨環境</span>
                                        <ul class="products-page__sublist">
                                            <li>iPhone・iPad：{{ ShopConstants.BROWSER_RECOMMENDED_VERSION_IOS }}</li>
                                            <li>Android：{{ ShopConstants.BROWSER_RECOMMENDED_VERSION_ANDROID }}</li>
                                        </ul>
                                    </li>
                                </ul>
                                <p>※タブレット端末では、機種や設定によって、スマートフォン版をご利⽤いただけない場合がございます。</p>
                                <p>※フィーチャーフォンには対応しておりません。ご了承ください。</p>
                                <p>※ご利⽤には、Cookie（クッキー）・JavaScript（ジャバスクリプト）を「有効」に設定する必要がございます。</p>
                            </div>
                        </transition>
                    </div>
                </div>

                <div class="products-page__footnote">
                    <span class="products-page__annotation">《<a class="products-page__inline-link" target="_blank" href="/tokusho">特定商取引に関する法律に基づく表示</a>》</span>
                </div>
            </section>
        </div>
    </section>
</template>

<style src="../../css/ui-primitives.css"></style>
<style scoped>
.products-page {
    padding: 72px 0 56px;
    background: var(--neutral-100);
}

.products-page__hero {
    margin: 0 auto 32px;
    max-width: 720px;
    text-align: center;
}

.products-page__title {
    margin: 0;
    font-size: clamp(1.85rem, 2.5vw, 2.4rem);
    font-weight: 700;
    color: var(--text-strong);
}

.products-page__lead {
    margin: 0;
    font-size: 1rem;
    line-height: 1.7;
    color: var(--text-body);
}

.products-page__alerts {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
}

.products-page__alert-card {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    width: 100%;
    max-width: 700px;
    padding: 20px 24px;
    border: 1px solid var(--semantic-info-border);
    box-shadow: 0 12px 28px rgba(var(--shadow-subtle-rgb), 0.08);
}

.products-page__alert-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--semantic-info-soft);
    color: var(--semantic-info-base);
    font-weight: 700;
    font-size: 0.95rem;
}

.products-page__alert-icon::before {
    content: 'i';
}

.products-page__alert-content {
    flex: 1;
    min-width: 0;
}

.products-page__alert-message {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.6;
    color: var(--text-body);
    white-space: pre-line;
}

.products-page__alert-links {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
}

.products-page__alert-link {
    color: var(--semantic-info-base);
    text-decoration: underline;
    font-weight: 600;
}

.products-page__alert-link:focus-visible {
    outline: 2px solid var(--semantic-info-base);
    outline-offset: 2px;
}

.products-page__alert-note {
    color: var(--text-muted);
}

.products-page__loading {
    display: flex;
    justify-content: center;
    padding: 32px 0;
}

.products-page__loading img {
    width: 52px;
    height: auto;
}

.products-page__plans {
    margin-bottom: 56px;
}

.products-page__plan-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
}

.products-page__plan-card {
    position: relative;
    min-height: 100%;
    padding: 32px 28px;
    border-color: var(--neutral-300);
    box-shadow: 0 12px 32px rgba(var(--shadow-subtle-rgb), 0.08);
}

.products-page__plan-card--highlight {
    border-color: var(--brand-primary-base);
    box-shadow: 0 18px 44px rgba(var(--brand-primary-rgb), 0.16);
}

.products-page__plan-card--disabled {
    opacity: 0.55;
    pointer-events: none;
}

.products-page__plan-card--disabled .products-page__plan-ribbon {
    display: none;
}

.products-page__plan-ribbon {
    position: absolute;
    top: -12px;
    left: 24px;
    background: var(--brand-primary-base);
    color: var(--surface-base);
    padding: 4px 16px;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.products-page__plan-head {
    text-align: left;
}

.products-page__plan-name {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--text-strong);
}

.products-page__plan-price {
    margin: 0;
    font-size: 1.9rem;
    font-weight: 700;
    color: var(--text-strong);
}

.products-page__plan-price-note {
    margin: 0;
    font-size: 0.95rem;
    color: var(--text-muted);
}

.products-page__plan-benefits {
    display: grid;
    gap: 12px;
    margin: 0 0 20px;
    padding: 0;
    list-style: none;
    color: var(--text-body);
}

.products-page__plan-benefits li {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.95rem;
    line-height: 1.6;
}

.products-page__plan-bullet {
    width: 10px;
    height: 10px;
    margin-top: 6px;
}

.products-page__plan-footer {
    margin-top: auto;
    text-align: left;
}

.products-page__plan-actions {
    display: grid;
    gap: 12px;
}

.products-page__plan-button {
    width: 100%;
}

.products-page__plan-billing {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.6;
    color: var(--text-body);
}

.products-page__plan-billing--muted {
    color: var(--text-muted);
}

.products-page__value-card {
    padding: 36px 40px;
    border-color: var(--neutral-300);
    box-shadow: 0 14px 36px rgba(var(--shadow-subtle-rgb), 0.1);
}

.products-page__value-intro {
    text-align: center;
}

.products-page__value-eyebrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 14px;
    border-radius: 999px;
    background: rgba(var(--brand-primary-rgb), 0.12);
    color: var(--brand-primary-base);
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.04em;
}

.products-page__value-benefits {
    display: grid;
    gap: 20px;
    margin: 0;
    padding: 0;
    list-style: none;
    grid-template-columns: 1fr;
}

.products-page__value-benefit {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: flex-start;
    gap: 16px;
    padding: 18px;
    border-radius: 14px;
    background: rgba(var(--surface-base-rgb), 0.96);
    border: 1px solid rgba(var(--brand-primary-rgb), 0.08);
    box-shadow: 0 14px 28px rgba(var(--shadow-overlay-rgb), 0.08);
}

.products-page__value-icon {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: var(--surface-base);
    box-shadow: 0 8px 20px rgba(var(--shadow-soft-rgb), 0.12);
    padding: 8px;
    display: block;
    object-fit: contain;
}

.products-page__value-copy {
    display: grid;
    gap: 6px;
}

.products-page__value-benefit-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--text-strong);
}

.products-page__value-benefit-text {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.6;
    color: var(--text-body);
}

@media (min-width: 768px) {
    .products-page__value-benefits {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 1200px) {
    .products-page__value-benefits {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}

.products-page__details {
    margin-top: 56px;
    padding: 32px;
}

.products-page__details-header {
    text-align: center;
}

.products-page__details-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-strong);
}

.products-page__accordion {
    display: grid;
    gap: 16px;
}

.products-page__accordion-item {
    background: var(--surface-base);
    border: 1px solid var(--neutral-300);
    border-radius: 16px;
    box-shadow: 0 10px 24px rgba(var(--shadow-subtle-rgb), 0.06);
    overflow: hidden;
}

.products-page__accordion-toggle {
    width: 100%;
    padding: 18px 24px;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    text-align: left;
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-strong);
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
}

.products-page__accordion-label {
    flex: 1;
}

.products-page__accordion-icon {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    transition: transform 0.2s ease;
}

.products-page__accordion-icon--open {
    transform: rotate(180deg);
}

.products-page__accordion-toggle:hover {
    background: var(--neutral-200);
}

.products-page__accordion-toggle:focus-visible {
    outline: 2px solid rgba(var(--brand-primary-rgb), 0.4);
    outline-offset: 2px;
}

.products-page__accordion-body {
    padding: 0 24px 20px;
    font-size: 0.95rem;
    line-height: 1.75;
    color: var(--text-body);
}

.products-page__accordion-body p {
    margin: 12px 0;
}

.products-page__inline-link {
    color: var(--semantic-info-base);
    text-decoration: underline;
    font-weight: 600;
}

.products-page__annotation {
    color: var(--text-muted);
}

.products-page__list {
    margin: 16px 0;
    padding-left: 20px;
    list-style: disc;
    color: var(--text-body);
}

.products-page__sublist {
    margin-top: 8px;
    padding-left: 20px;
    list-style: disc;
}

.products-page__footnote {
    padding-top: 16px;
    border-top: 1px solid var(--neutral-200);
    text-align: center;
    font-size: 0.85rem;
    color: var(--text-muted);
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
}

.products-accordion-enter-active,
.products-accordion-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.products-accordion-enter,
.products-accordion-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

@media (max-width: 991px) {
    .products-page {
        padding: 64px 0 48px;
    }

    .products-page__plan-card {
        padding: 28px 24px;
    }
}

@media (max-width: 575px) {
    .products-page {
        padding: 56px 0 40px;
    }

    .products-page__hero {
        margin-bottom: 24px;
    }

    .products-page__title {
        font-size: 1.75rem;
    }

    .products-page__lead {
        font-size: 0.95rem;
    }

    .products-page__alert-card {
        padding: 18px;
    }

    .products-page__plan-card {
        padding: 24px 20px;
    }

    .products-page__value-card {
        padding: 28px 20px;
    }

    .products-page__value-benefit {
        grid-template-columns: 1fr;
    }

    .products-page__value-icon {
        width: 56px;
        height: 56px;
        margin: 0 auto;
    }

    .products-page__value-copy {
        text-align: center;
    }

    .products-page__details {
        padding: 24px 20px;
    }

    .products-page__accordion-toggle {
        padding: 16px 18px;
    }

    .products-page__accordion-body {
        padding: 0 18px 18px;
    }
}
</style>

<script>
import { mapActions, mapGetters } from "vuex";
import Utils from '../../js/helpers/Utils';
import PageControl from '../../js/helpers/PageControl';
import ShopConstants from '../../js/helpers/ShopConstants';
import AppSession from '../../js/session/AppSession';
import DialogMixin from '../../js/helpers/mixins/DialogMixin';
import GoogleTagManager from '../../js/thirdparty/analytics/GoogleTagManager';
import TransitionModel from '../../js/models/secure/TransitionModel';

export default {
    data(){
        return {
            eowp: {
                monthly: {
                    itemId: '',
                    subscription_status: 0,
                    ref: "EOWP_MONTHLY_ITEM",
                    basePrice: 0,
                    merchantPrice: 0,
                    disabled: false,
                    notif: '',
                    expired: false,
                    subscribed_notif: 'すでに月額コースをご利用中です。\n「年額コースに変更」をクリックすると、年額コースへお切り替えいただけます。'
                },
                yearly: {
                    itemId: '',
                    subscription_status: 0,
                    ref: "EOWP_YEARLY_ITEM",
                    basePrice: 0,
                    merchantPrice: 0,
                    monthlyPrice: 0,
                    disabled: false,
                    notif: '',
                    subscribed_notif: 'すでに年額コースをご利用中です。'
                },
                yearlyS: { // Old discounted Annual Subscription EOWP(S)
                    itemId: '',
                    subscription_status: 0,
                    ref: "EOWP_YEARLY_S_ITEM",
                    basePrice: 0,
                    merchantPrice: 0,
                    disabled: false,
                    notif: '',
                    subscribed_notif: 'すでに年額コースをご利用中です。'
                },
                other: {
                    result: {},
                    active: false,
                    chargeTypeDescription: '',
                    notif: 'すでに「英辞郎 on the WEB Pro」をご利用中です。購入元： '
                },
                active: [1,5],
                paymentFailureSubscriptionFlag: 2, // actual useStatus value flag for 'PAYMENT FAILURE' on shop DB
                expiredSubscription: 2,
                cancelledSubscription: 3,
                suspendedSubscription: 4,
                upgradedSubscription: 5,
                chargeTypeDescriptions: {
                    0: '',
                    1: 'Shop',
                    2: 'Apple',
                    3: 'Google',
                    4: '法人／団体購入'
                },
                showButtons: false,
                subscriberTypeMessage: {
                    ERR2: {
                        title: "エラーが発生しました", //An Error Occurred
                        body: "このアカウントで現在のサブスクリプションを見つけることができません。一度ログアウトして、再度サインインしてください。", // Unable to locate the current subscription under this account. Please logout and sign in.
                    },
                    DEFAULT_ERROR: {
                        title: '予期せぬエラーが発生しました', //Unexpected Error Occurred
                        body: '契約内容の更新に失敗しました。以下の情報を添えて管理者にお問い合わせください。', // Failed on updates on given subscription. Please contact the administrator with the following information.
                    }
                },
            },
            pageLoadingIndicator: true,
            notifs: {
                3: '自動更新が無効になっています。再開はこちらから： ',
                4: 'お支払いでエラーが発生しました。カード情報変更はこちらから： ',
                5: '月額コースから年額コースへの移行期間中です。こちらでご確認いただけます： '
            },
            NETWORK_ACCESS_ONLY: true,
            dialogParams: {
                refreshPageNetworkErrorOptions: {
                    loader: false,
                    okText: '閉じる', //Close
                    cancelText: '閉じる', //Close
                    animation: 'zoom',
                    backdropClose: true
                }
            },
            ShopConstants: ShopConstants,
            accordionState: {
                trial: true,
                featureDifference: true,
                payment: true,
                cancellation: true,
                overseas: true,
                environment: true,
            },
            valueCardBenefits: [
                {
                    image: '/images/interestCard1.svg',
                    alt: '広告なしアイコン',
                    title: '広告なしで快適検索​',
                    description: 'Proプランは広告なし。表示スピードと一覧性がアップして、快適に検索できます。​'
                },
                {
                    image: '/images/interestCard2.svg',
                    alt: '例文データアイコン',
                    title: '140万件以上の英和例文',
                    description: '無料プランより100万件以上多い例文データを参照でき、学習の幅が広がります。'
                },
                {
                    image: '/images/interestCard3.svg',
                    alt: '単語帳アイコン',
                    title: '2万件登録できる単語帳',
                    description: '単語帳は20,000件まで保存可能。語彙を整理して繰り返し復習できます。'
                },
                {
                    image: '/images/interestCard4.svg',
                    alt: '検索サポート機能アイコン',
                    title: '便利な検索サポート機能',
                    description: '小窓モードやあいまい検索など、検索​を快適にするツールが使い放題です。'
                }
            ],
            priceComparison: {
                monthlyYearlyTotal: '0',
                yearlySavings: '0',
            },
        };
    },
    async created(){
        this.clearPaymentSuccessFlag();
        this.clearChangeCC();
        this.clearUpdateCCResult();
        this.clearTransitionStates();
        if(!await this.checkActiveSession(this.NETWORK_ACCESS_ONLY)) return;

        // Determine if this user has pending subscription for sync - hard redirect to account page instead
        await this.pendingSubscriptionSync({userId: this.sessionDetails.userId, sessionId: this.sessionDetails.sessionId}).then(res => {
            if(res.SYNC && res.SYNC > 0){ window.location.href = "/"; return; }
        });

        // Check user session if it's still active, else wipe out session details
        if(this.userSessionId){
            let appSession = new AppSession();
            await appSession.isAuthenticated().then(status => {
                if(!status){
                    this.clearUserStates();
                }
            });
        }

        // Pull items and update base prices
        await this.retrieveProducts().then(res => {
            console.log(res, "products");
            if(!res.records) return;
            let monthlyData = res.records.filter(obj => obj.itemType == this.eowp.monthly.ref)[0];
            let yearlyData = res.records.filter(obj => obj.itemType == this.eowp.yearly.ref)[0];
            let yearlySData = res.records.filter(obj => obj.itemType == this.eowp.yearlyS.ref)[0];

            let productTypes = {};
            productTypes[monthlyData.csMatNo] = "MONTHLY";
            productTypes[yearlyData.csMatNo] = "YEARLY";
            this.setProductTypes(productTypes);
            
            this.eowp.monthly.itemId = monthlyData.csMatNo;
            this.eowp.monthly.basePrice = Utils.numberToCurrency(monthlyData.productPrice);
            this.eowp.monthly.merchantPrice = Utils.numberToCurrency(monthlyData.merchantProductPrice);
            this.eowp.yearly.itemId = yearlyData.csMatNo;
            this.eowp.yearly.basePrice = Utils.numberToCurrency(yearlyData.productPrice);
            this.eowp.yearly.merchantPrice = Utils.numberToCurrency(yearlyData.merchantProductPrice);
            this.eowp.yearly.monthlyPrice = Utils.numberToCurrency(Math.round(yearlyData.merchantProductPrice / 12));
            const monthlyAnnualTotal = monthlyData.merchantProductPrice * 12;
            const yearlySavings = Math.max(monthlyAnnualTotal - yearlyData.merchantProductPrice, 0);
            this.priceComparison.monthlyYearlyTotal = Utils.numberToCurrency(monthlyAnnualTotal);
            this.priceComparison.yearlySavings = Utils.numberToCurrency(yearlySavings);

            this.eowp.yearlyS.itemId = yearlySData.csMatNo;
        });

        // Check user subscription
        if(this.userSessionId){
            await this.fetchSessionDetails(this.userSessionId).then(async res => {
                if(!res.records) return;
                let userId = res.records.userId;

                await this.retrieveSubscription({custId: userId, itemId: this.eowp.monthly.itemId}).then(res => {
                    console.log(res, "monthly");
                    this.eowp.monthly.subscription_status = this.subscriptionLogic(res.subscription_status, res.subscription_expired);
                    let notification = this.notifs[this.eowp.monthly.subscription_status];
                    this.eowp.monthly.expired = res.subscription_expired;
                    this.eowp.monthly.notif = Utils.isEmpty(notification) ? '' : this.getNotification(notification, '月額コース', this.eowp.monthly.subscription_status);
                    this.eowp.monthly.disabled = (this.isActive(this.eowp.monthly.subscription_status) || !Utils.isEmpty(this.eowp.monthly.notif));

                });
                await this.retrieveSubscription({custId: userId, itemId: this.eowp.yearly.itemId}).then(res => {
                    console.log(res, "yearly");
                    this.eowp.yearly.subscription_status = this.subscriptionLogic(res.subscription_status, res.subscription_expired);
                    let notification = this.notifs[this.eowp.yearly.subscription_status];
                    this.eowp.yearly.notif = Utils.isEmpty(notification) ? '' : this.getNotification(notification, '年額コース', this.eowp.yearly.subscription_status);
                    this.eowp.yearly.disabled = (this.isActive(this.eowp.yearly.subscription_status) || !Utils.isEmpty(this.eowp.yearly.notif));

                    //Include the monthly if yearly is active
                    if(this.eowp.yearly.disabled){
                        this.eowp.monthly.disabled = true;
                    }

                    //Override everything if monthly is detected to have upgraded
                    if(!this.eowp.yearly.disabled && this.eowp.monthly.subscription_status == this.eowp.upgradedSubscription){
                        this.eowp.yearly.disabled = true;
                    }

                    // Logic #131 For user with cancelled monthly subscription, disable yearly item until subscriber's monthly subscription is resumed
                    if(!this.eowp.yearly.disabled && this.eowp.monthly.subscription_status == this.eowp.cancelledSubscription && !this.eowp.monthly.subscription_expired){
                        this.eowp.yearly.disabled = true;
                    }
                });

                // Let's consider EOWP(S) version here. We may not let subscribers able to purchase if they have active EOWP(S) subscription
                // And let it reflect to yearly if standard yearly EOWP subscription is not active
                if(!this.eowp.monthly.disabled && !this.eowp.yearly.disabled){
                    await this.retrieveSubscription({custId: userId, itemId: this.eowp.yearlyS.itemId}).then(res => {
                        console.log(res, "yearlyS");
                        this.eowp.yearly.subscription_status = this.subscriptionLogic(res.subscription_status, res.subscription_expired);
                        let notification = this.notifs[this.eowp.yearly.subscription_status];
                        this.eowp.yearly.notif = Utils.isEmpty(notification) ? '' : "年額コース - " + notification;
                        this.eowp.yearly.disabled = (this.isActive(this.eowp.yearly.subscription_status) || !Utils.isEmpty(this.eowp.yearly.notif));

                        //Include the monthly if yearly is active
                        if(this.eowp.yearly.disabled){
                            this.eowp.monthly.disabled = true;
                        }
                    });
                }

                await this.retrieveOtherSubscription({custId: userId}).then(res => {
                    console.log(res, "other");
                    if(!res.records) return;
                    if(res.records.result.inPeriod && res.records.result.chargeType > 0){
                        if((!this.eowp.monthly.disabled && !this.eowp.yearly.disabled && res.records.result.chargeType == 1) || res.records.result.chargeType != 1){
                            this.eowp.other.result = res.records.result;
                            this.eowp.other.active = res.records.result.inPeriod;
                            this.eowp.other.chargeTypeDescription = this.eowp.chargeTypeDescriptions[res.records.result.chargeType];
                        }
                    }
                });
            });
        }

        // LOGIC #123 - force subscriber to pay first the pending monthly payment: current monthly subscription with use_status=2
        if(this.eowp.monthly.subscription_status == this.eowp.suspendedSubscription){
            this.eowp.yearly.disabled = true;
        }

        // LOGIC #466 - Block any purchase when user's current monthly subscription is in payment failure state and not expired
        await this.getCurrentSubscription(this.sessionDetails.userId).then(() => {
            if(!Utils.isEmpty(this.currentSubscription) && this.currentSubscription.csMatNo == this.eowp.monthly.itemId){
                if(this.currentSubscription.useStatus == this.eowp.paymentFailureSubscriptionFlag && !this.eowp.monthly.expired){
                    let notification = this.notifs[this.eowp.suspendedSubscription];
                    this.eowp.monthly.notif = Utils.isEmpty(notification) ? '' : "月額コース - " + notification;
                    this.eowp.monthly.disabled = true;
                    this.eowp.yearly.disabled = true;
                }
            }
        });

        this.eowp.showButtons = true;
        this.pageLoadingIndicator = false;

        let userValue = !Utils.isEmpty(this.sessionDetails) ? this.sessionDetails.userId : 'visitor';
        let sessionValue = !Utils.isEmpty(this.sessionDetails) ? this.sessionDetails.sessionId : 'visitor';
        this.logActivity({type: "info", message: " Loaded Products Page with userId: " + userValue + " and sessionId: " + sessionValue
        + " with products loaded: MONTHLY - " + this.eowp.monthly.disabled + " and YEARLY - " + this.eowp.yearly.disabled});

        if(!Utils.isEmpty(this.cancelPurchase)){
            let cancelledItem = [];
            cancelledItem.push(this.cancelPurchase);
            let data = {
                currency: 'JP',
                items: cancelledItem
            };
            this.triggerActionEvent({event: GoogleTagManager.EVENT_REMOVE_FROM_CART, data: data});
        }
        this.setCancelPurchase('');
    },
    watch: {
        // reload the page when a signout event from another tab is detected
        // - this will reset the error messages/disabled buttons accordingly 
        sessionDetails: (sessionValue) => {
            new PageControl(this).validateSession(sessionValue, true);
        }
    },
    computed: {
        ...mapGetters({
            products: 'ncs/products',
            chosenItem: 'ncs/chosen_item',
            currentSubscription: 'ncs/current_subscription',
            userSessionId: 'user/session',
            cancelPurchase: 'user/cancel_purchase',
            sessionDetails: 'user/session_details',
            subscriber: 'transition/subscriber_type',
        }),
        buyAnnualButtonText(){
            return this.eowp.monthly.disabled && !this.eowp.yearly.disabled && !this.eowp.other.active ? '年額コースに変更' : '年額で申し込む';
        },
        notificationQueue(){
            const queue = [];
            const pushNotification = (condition, message, priority) => {
                if(condition && !Utils.isEmpty(message)){
                    queue.push({ priority, message: message.trim() });
                }
            };

            if(!Utils.isEmpty(this.eowp.other.chargeTypeDescription)){
                const otherMessage = `${this.eowp.other.notif}${this.eowp.other.chargeTypeDescription} アカウントページをご確認ください。`;
                pushNotification(true, otherMessage, 0);
            }

            pushNotification(!Utils.isEmpty(this.eowp.yearly.notif), this.eowp.yearly.notif, 1);
            pushNotification(!Utils.isEmpty(this.eowp.monthly.notif), this.eowp.monthly.notif, 2);
            pushNotification(this.eowp.monthly.subscription_status == 1, this.eowp.monthly.subscribed_notif, 3);
            pushNotification(this.eowp.yearly.subscription_status == 1 && this.eowp.monthly.subscription_status != this.eowp.upgradedSubscription, this.eowp.yearly.subscribed_notif, 4);

            return queue.sort((a, b) => a.priority - b.priority);
        },
        activeNotification(){
            return this.notificationQueue.length ? this.notificationQueue[0] : null;
        },
        additionalNotifications(){
            return this.notificationQueue.length > 1 ? this.notificationQueue.slice(1) : [];
        }
    },
    methods: {
        ...mapActions({
            getCurrentSubscription: 'ncs/getCurrentSubscription',
            retrieveProducts: 'ncs/retrieveProducts',
            retrieveSubscription: 'ncs/retrieveSubscription',
            setProductTypes: 'ncs/setProductTypes',
            retrieveOtherSubscription: 'ncs/retrieveOtherSubscription',
            setChosenItem: 'ncs/setChosenItem',
            ncsClearState: 'ncs/clearStates',
            fetchSessionDetails: 'user/fetchSessionDetails',
            clearUserStates: 'user/clearStates',
            setPathIntention: 'user/setPathIntention',
            setCancelPurchase: 'user/setCancelPurchase',
            logActivity: 'log/logActivity',
            triggerActionEvent: 'ga/triggerActionEvent',
            trackClickEvent: 'ga/trackClickEvent',
            clearPaymentSuccessFlag: 'pg/clearSuccessFlag',
            clearChangeCC: 'pg/setUpdateCC',
            clearUpdateCCResult: 'pg/clearUpdateCCResult',
            pendingSubscriptionSync: 'pg/pendingSubscriptionSync',
            subscriberType: 'transition/subscriberType',
            setTransitionUserId: 'transition/setTransitionUserId',
            setTransitionCsMatNo: 'transition/setTransitionCsMatNo',
            setTransitionCsOrderNo: 'transition/setTransitionCsOrderNo',
            setTransitionType: 'transition/setTransitionType',
            clearTransitionStates: 'transition/clearStates',
        }),
        isPlanDisabled(plan){
            return (plan && plan.disabled) || this.eowp.other.active;
        },
        buttonTooltip(plan){
            return this.isPlanDisabled(plan) ? '現在お手続きいただけません。アカウントページをご確認ください。' : '';
        },
        planCardClasses(type){
            const plan = this.eowp[type] || {};
            const classes = {
                'products-page__plan-card--disabled': this.isPlanDisabled(plan),
            };
            if(type === 'yearly' && !this.isPlanDisabled(plan)){
                classes['products-page__plan-card--highlight'] = true;
            }
            return classes;
        },
        toggleAccordion(section){
            if(!section) return;
            const currentState = this.accordionState[section];
            this.$set(this.accordionState, section, !currentState);
        },
        isActive(flag){
            return (this.eowp.active.indexOf(flag) >= 0);
        },
        async submit(itemId, eventName){
            //Zeus to Stripe Transition Implementation
            if(eventName == 'yearly_product_click' && this.currentSubscription && this.currentSubscription.csMatNo == '9111620'){
                let userId = this.currentSubscription.custId;
                let csOrderNo = this.currentSubscription.csOrderNo;
                let vm = this;
                await this.subscriberType({userId: userId, csOrderNo: csOrderNo}).then(res => {
                    if(res.status_code != "ERR0") {
                        let errorRef = vm.eowp.subscriberTypeMessage[res.status_code];
                        if(!errorRef) errorRef = vm.eowp.subscriberTypeMessage['DEFAULT_ERROR'];

                        let statusCodes = new Array();
                        if(res.status_code) statusCodes.push("StatusCode: " + res.status_code);
                        if(res.responseCode) statusCodes.push("ResponseCode: " + res.responseCode);
                        if(res.resultCode) statusCodes.push("ResultCode: " + res.resultCode);
                        let finalStatusCodes = statusCodes.length > 0 ? statusCodes.join(",") : "";

                        vm.$dialog.alert({
                            title: errorRef.title,
                            body: errorRef.body + " [" + finalStatusCodes + "]",
                        }, vm.dialogParams.refreshPageNetworkErrorOptions)
                        .then(() => window.location.reload())
                        .catch(() => {});
                        return;
                    }
                });
                if(Utils.isEmpty(this.subscriber)) return;
                if(this.subscriber == TransitionModel.OLD_SUBSCRIBER){
                    this.setTransitionUserId(this.currentSubscription.custId);
                    this.setTransitionCsMatNo(this.currentSubscription.csMatNo);
                    this.setTransitionCsOrderNo(this.currentSubscription.csOrderNo);
                    this.setTransitionType(TransitionModel.TRANSITION_TYPE_UPGRADE);
                    this.$router.push("/transition");
                    return;
                }
            } // End - Zeus to Stripe Transition Implementation

            await this.trackClickEvent({event: eventName, path: this.$route.path, title: this.$route.meta.title});

            await this.setChosenItem(itemId);
            await this.setPathIntention('/products');
            this.$router.push('/basket');
        },
        subscriptionLogic(status, expired){
            // "status" definitions in backend NcsDB - ITEM_SUBSCRIPTION_XXXXX
            // Regardless of the transformed status, once it's expired, it has to be in expired state
            return !Utils.isEmpty(expired) && expired ? this.eowp.expiredSubscription : status;
        },
        getNotification(notif, course, flag){
            return flag == this.eowp.upgradedSubscription ? notif : course + ' - ' + notif;
        }
    },
    mixins: [
        DialogMixin
    ]
};
</script>
